/**
 * PocketBase auth adapter.
 *
 * Implements Keystone's AuthAdapter interface so the rest of the app
 * (lib/api, lib/realtime, login/signup pages) is decoupled from PB
 * specifics. To swap to a different backend, point lib/auth/index.ts
 * at a different adapter — no other code changes needed.
 *
 * Auth *mutations* (login/signUp/refresh/resetPassword) go through
 * `pbFor("auth")` so the PB→Django cutover flips them via the same
 * per-scope router as every other scope (plan §10). `pbFor` shares the
 * one global `pb.authStore`, so token/record reads, `onChange`,
 * `clear` and `updateRecord` stay on the global store and remain
 * consistent regardless of which backend served the mutation. Inert
 * until `auth` ∈ VITE_DJANGO_ROUTES — `pbFor("auth")` returns the
 * global PB client until then.
 */

import { pb } from "../pb";
import { pbFor } from "@/lib/api/router";
import type {
  AuthAdapter,
  AuthChangeListener,
  AuthRecord,
} from "./authAdapter";

class PocketBaseAdapter implements AuthAdapter {
  private listeners = new Set<AuthChangeListener>();

  constructor() {
    // PB's authStore.onChange fires after rehydrate + every login/logout/refresh.
    pb.authStore.onChange((token, model) => {
      const record = (model as AuthRecord | null) ?? null;
      for (const cb of this.listeners) {
        try { cb(token || "", record); }
        catch { /* one bad subscriber shouldn't break the rest */ }
      }
    }, true);
  }

  // ── Read API (mirrors PB authStore) ──────────────────────────────
  get isValid(): boolean { return pb.authStore.isValid; }
  get token(): string    { return pb.authStore.token || ""; }
  get record(): AuthRecord | null {
    return (pb.authStore.record as AuthRecord | null) ?? null;
  }

  // ── Subscriptions ────────────────────────────────────────────────
  onChange(callback: AuthChangeListener): () => void {
    this.listeners.add(callback);
    return () => { this.listeners.delete(callback); };
  }

  // ── Mutations ────────────────────────────────────────────────────
  async login(identifier: string, password: string): Promise<AuthRecord> {
    const result = await pbFor("auth").collection("users").authWithPassword(identifier, password);
    return result.record as unknown as AuthRecord;
  }

  /** PB-specific extension — used by SignUpForm to create + auto-login. */
  async signUp(
    email: string,
    password: string,
    displayName?: string,
  ): Promise<AuthRecord> {
    await pbFor("auth").collection("users").create({
      email,
      password,
      passwordConfirm: password,
      displayName: displayName || email.split("@")[0],
      role: "Creator",
      emailVisibility: true,
    });
    return this.login(email, password);
  }

  clear(): void {
    pb.authStore.clear();
  }

  async refresh(): Promise<void> {
    if (!pb.authStore.isValid) return;
    await pbFor("auth").collection("users").authRefresh();
  }

  async resetPassword(email: string): Promise<void> {
    await pbFor("auth").collection("users").requestPasswordReset(email);
  }

  updateRecord(patch: Partial<AuthRecord>): void {
    if (!pb.authStore.record) return;
    const merged = { ...pb.authStore.record, ...patch };
    pb.authStore.save(pb.authStore.token, merged);
  }

  /**
   * For api.ts compatibility — PB tokens are long-lived JWTs that the
   * SDK refreshes lazily via authRefresh(). We return the cached token;
   * call refresh() explicitly when you suspect it's stale.
   */
  async getValidToken(): Promise<string> {
    return pb.authStore.token || "";
  }
}

const adapter: AuthAdapter & { signUp?: PocketBaseAdapter["signUp"] } =
  new PocketBaseAdapter();

export default adapter;
export { PocketBaseAdapter };
